import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { coerceHttpParams } from 'src/app/shared/flight-log-api/shared/create-request-options';
import { AppState } from 'src/store';
import {
  DeleteEmployee,
  UpsertEmployee,
  UpsertEmployees,
} from 'src/store/employee/employee.actions';
import { assertNotNullOrWhitespace } from 'src/util/values';
import { FLIGHT_LOG_API_CONFIG, FlightLogApiConfiguration } from '../flight-log-api-configuration';
import { EmployeeDto } from './employee-client.model';

export interface EmployeePrdPermissionsParams {
  id: string;
  airCarrierIds: string[];
  userRoles: string[];
}

@Injectable({ providedIn: 'root' })
export class EmployeeHttpClient {
  constructor(
    @Inject(FLIGHT_LOG_API_CONFIG) private config: FlightLogApiConfiguration,
    private http: HttpClient,
    private _store: Store<AppState>,
  ) {}

  public getById(id: string): Observable<EmployeeDto | undefined> {
    assertNotNullOrWhitespace(id, 'employeeId');
    return this.http.get<EmployeeDto | undefined>(this._url(`employees/${id}`)).pipe(
      tap((employee) => {
        this._store.dispatch(
          employee != null ? new UpsertEmployee({ employee }) : new DeleteEmployee({ id }),
        );
      }),
    );
  }

  public getByEmail(email: string): Observable<EmployeeDto | undefined> {
    assertNotNullOrWhitespace(email, 'employeeEmail');
    return this.http
      .get<EmployeeDto | undefined>(this._url(`employeeByEmail?emailAddress=${email}`))
      .pipe(
        tap((employee) => {
          if (employee != null) {
            this._store.dispatch(new UpsertEmployee({ employee }));
          }
        }),
      );
  }

  public getByEmployeeId(employeeId: string): Observable<EmployeeDto | undefined> {
    assertNotNullOrWhitespace(employeeId, 'employeeId');
    return this.http
      .get<EmployeeDto | undefined>(this._url(`employeeByEmployeeId?employeeId=${employeeId}`))
      .pipe(
        tap((employee) => {
          if (employee != null) {
            this._store.dispatch(new UpsertEmployee({ employee }));
          }
        }),
      );
  }

  public getAll(
    params: {
      $top?: number;
      $skip?: number;
      searchTerm?: string;
      id?: string;
      companyId?: string;
      includeDispatched?: boolean;
      searchMode?: 'Contains' | 'ContainsFulltext' | 'StartsWith';
    } = {},
  ): Observable<EmployeeDto[]> {
    return this.http
      .get<EmployeeDto[]>(this._url('employees'), {
        params: coerceHttpParams({
          $top: 10,
          ...params,
        }),
      })
      .pipe(
        tap((employees) => {
          this._store.dispatch(new UpsertEmployees({ employees }));
        }),
      );
  }

  public updatePrdPermissions(values: EmployeePrdPermissionsParams) {
    return this.http.post(this._url(`employees`), values);
  }

  private _url(path: string) {
    return this.config.baseUri + '/api/' + path;
  }
}
